import { useEffect, useRef, useState, type FC } from 'react';
import { MfaMethod } from '@shared/services';
import { EmailStepView } from './EmailStepView';
import { Card } from '@/components/ui';
import {
  useMfaConfirmMutation,
  useMfaEnrollMutation,
  useMfaRequestEmailCodeMutation,
  useMfaVerifyMutation,
  type MfaVerifyResponse,
} from '@/shared/api/auth';
import { getLoginFlowError, isMfaSessionExpiredError, useCountdown } from '@/shared/utils';

const RESEND_COOLDOWN_SECONDS = 30;

interface Props {
  mfaPending: string;
  mode: 'enroll' | 'verify';
  /**
   * Receives the raw success result. In `verify` mode this is the dual-outcome verify response
   * (session or accounts-proof); in `enroll` mode it is the confirm view (a session, which the
   * enrollment page discards), assignable to the same union.
   */
  onSuccess: (view: MfaVerifyResponse) => void;
  onExpired: () => void;
  onBack: () => void;
}

/**
 * Email one-time-code step, shared by enrollment and verification. The two differ only in which
 * endpoints send and submit the code — enrollment `enroll{email}`/`confirm`, verification
 * `email/request`/`verify` — so the UI is identical. The code is requested once on mount.
 */
export const EmailStep: FC<Props> = ({ mfaPending, mode, onSuccess, onExpired, onBack }) => {
  const isEnroll = mode === 'enroll';

  const { enroll } = useMfaEnrollMutation();
  const { confirm, isConfirming } = useMfaConfirmMutation();
  const { requestEmailCode } = useMfaRequestEmailCodeMutation();
  const { verify, isVerifying } = useMfaVerifyMutation();

  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isResending, setIsResending] = useState(false);
  const sentRef = useRef(false);
  const { secondsLeft: resendCountdown, start: startResendCooldown } = useCountdown();

  const sendCode = async () => {
    if (isEnroll) {
      await enroll({ mfaPending, method: MfaMethod.EMAIL });
    } else {
      await requestEmailCode({ mfaPending });
    }
  };

  useEffect(() => {
    if (sentRef.current) {
      return;
    }

    sentRef.current = true;

    void (async () => {
      try {
        await sendCode();
        startResendCooldown(RESEND_COOLDOWN_SECONDS);
      } catch (caught) {
        if (isMfaSessionExpiredError(caught)) {
          onExpired();

          return;
        }

        setError(getLoginFlowError(caught));
      }
    })();
    // Send exactly once on mount; `sendCode` closes over stable values.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async () => {
    setError(null);

    try {
      const view = isEnroll ? await confirm({ mfaPending, code }) : await verify({ mfaPending, code });
      onSuccess(view);
    } catch (caught) {
      if (isMfaSessionExpiredError(caught)) {
        onExpired();

        return;
      }

      setError(getLoginFlowError(caught));
    }
  };

  const resend = async () => {
    setError(null);
    setIsResending(true);

    try {
      await sendCode();
      startResendCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (caught) {
      if (isMfaSessionExpiredError(caught)) {
        onExpired();

        return;
      }

      setError(getLoginFlowError(caught));
    } finally {
      setIsResending(false);
    }
  };

  const isSubmitting = isConfirming || isVerifying;

  return (
    <>
      <Card.BackButton onClick={onBack} testId="mfa-back-button" />
      <Card.Title
        title="Check your email"
        description="We sent a 6-digit code to your email address. Enter it below to continue."
      />
      <EmailStepView
        code={code}
        onCodeChange={setCode}
        onSubmit={submit}
        isSubmitting={isSubmitting}
        error={error}
        onResend={resend}
        isResending={isResending}
        resendCountdown={resendCountdown}
      />
    </>
  );
};
